import { useCallback, useEffect, useRef } from 'react';

import { getSelectionSignature, toSelectionUpdate } from './evaluatorSelection';

import type { SelectionContent } from './evaluatorSelection';
import type { FeedbackPrivacyValue } from '@/constants/review/FeedbackPrivacy';
import type {
    CycleEvaluatorCriteria,
    CycleEvaluatorSelection,
} from '@/types/cycle-evaluation/CycleEvaluatorSelection';

import { useUpdateCycleEvaluatorSelection } from '@/api/cycle-evaluation-services/cycle-evaluator-selection';
import { useDebounce } from '@/hooks/useDebounce';

const AUTOSAVE_DELAY_MS = 500;

interface UseAutosaveEvaluatorSelectionParams {
    isReadOnly: boolean;
    isHydrated: boolean;
    hasUserEdited: boolean;
    cycleEvaluationId: string | undefined;
    storedSelection: CycleEvaluatorSelection | undefined;
    cycleEvaluatorCriterias: CycleEvaluatorCriteria[];
    suggestedReviewerCount: number;
    feedbackPrivacy: FeedbackPrivacyValue;
    commentRepliesEnabled: boolean;
    isFeedbackPrivacyLocked: boolean;
    onAutosaveRefused: () => void;
}

const useAutosaveEvaluatorSelection = ({
    isReadOnly,
    isHydrated,
    hasUserEdited,
    cycleEvaluationId,
    storedSelection,
    cycleEvaluatorCriterias,
    suggestedReviewerCount,
    feedbackPrivacy,
    commentRepliesEnabled,
    isFeedbackPrivacyLocked,
    onAutosaveRefused,
}: UseAutosaveEvaluatorSelectionParams) => {
    const { mutateAsync: updateCycleEvaluatorSelection } = useUpdateCycleEvaluatorSelection();

    // No signature before hydration: an empty seed makes an emptied draft read as unchanged.
    const debouncedSignature = useDebounce(
        isHydrated
            ? getSelectionSignature({
                  cycleEvaluatorCriterias,
                  suggestedReviewerCount,
                  feedbackPrivacy,
                  commentRepliesEnabled,
              })
            : undefined,
        AUTOSAVE_DELAY_MS,
    );

    const savedSignatureRef = useRef<string | undefined>(undefined);

    // What was saved belongs to one selection: another one is compared with what it stores.
    const storedSelectionId = storedSelection?.id;
    useEffect(() => {
        savedSignatureRef.current = undefined;
    }, [storedSelectionId]);

    const latestRef = useRef({
        isReadOnly,
        isHydrated,
        hasUserEdited,
        cycleEvaluationId,
        storedSelection,
        cycleEvaluatorCriterias,
        suggestedReviewerCount,
        feedbackPrivacy,
        commentRepliesEnabled,
        isFeedbackPrivacyLocked,
        onAutosaveRefused,
        updateCycleEvaluatorSelection,
    });
    latestRef.current = {
        isReadOnly,
        isHydrated,
        hasUserEdited,
        cycleEvaluationId,
        storedSelection,
        cycleEvaluatorCriterias,
        suggestedReviewerCount,
        feedbackPrivacy,
        commentRepliesEnabled,
        isFeedbackPrivacyLocked,
        onAutosaveRefused,
        updateCycleEvaluatorSelection,
    };

    // Next replaces the whole object too, so a pending write here would land after it and undo it.
    const cancelPendingAutosave = useCallback((selection: SelectionContent) => {
        savedSignatureRef.current = getSelectionSignature(selection);
    }, []);

    const restorePendingAutosave = useCallback(() => {
        savedSignatureRef.current = undefined;
    }, []);

    useEffect(() => {
        const {
            isReadOnly: readOnly,
            isHydrated: hydrated,
            hasUserEdited: edited,
            cycleEvaluationId: evaluationId,
            storedSelection: stored,
            cycleEvaluatorCriterias: criterias,
            suggestedReviewerCount: reviewerCount,
            feedbackPrivacy: privacy,
            commentRepliesEnabled: comments,
            isFeedbackPrivacyLocked: areSettingsLocked,
            onAutosaveRefused: reportRefusal,
            updateCycleEvaluatorSelection: save,
        } = latestRef.current;

        if (!edited || readOnly || !hydrated || !evaluationId || !stored) {
            return;
        }

        const draftSelection: SelectionContent = {
            cycleEvaluatorCriterias: criterias,
            suggestedReviewerCount: reviewerCount,
            feedbackPrivacy: privacy,
            commentRepliesEnabled: comments,
        };

        if ((savedSignatureRef.current ?? getSelectionSignature(stored)) === debouncedSignature) {
            return;
        }

        // A refused autosave stays unsaved; the page says what it can tell, and Next reports the rest.
        void save({
            id: stored.id,
            cycleEvaluationId: evaluationId,
            ...toSelectionUpdate({
                selection: draftSelection,
                isFeedbackPrivacyLocked: areSettingsLocked,
            }),
        })
            .then(() => {
                savedSignatureRef.current = getSelectionSignature(draftSelection);
            })
            .catch(reportRefusal);
    }, [debouncedSignature]);

    return { cancelPendingAutosave, restorePendingAutosave };
};

export { useAutosaveEvaluatorSelection, AUTOSAVE_DELAY_MS };
